import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserShield,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaShieldAlt,
  FaSync,
  FaFilter,
  FaTimes,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import RoleForm from "../../Components/forms/RoleForm";
import RolePermissionsModal from "./RolePermissionsModal";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function RoleList() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissionsRole, setPermissionsRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cardFilter, setCardFilter] = useState("all"); // "all" | "active" | "inactive" | "policy"
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [stats, setStats] = useState({
    totalCount: 0,
    activeCount: 0,
    inactiveCount: 0,
    accessPolicy: "RBAC Enabled",
  });
  const { token } = useAuth();

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/roles`, {
        params: {
          status: cardFilter,
          search: search,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = res.data?.data || [];
      setRoles(data);
      setStats({
        totalCount: res.data?.totalCount !== undefined ? res.data.totalCount : data.length,
        activeCount: res.data?.activeCount !== undefined ? res.data.activeCount : data.filter((r) => r.isActive !== false).length,
        inactiveCount: res.data?.inactiveCount !== undefined ? res.data.inactiveCount : data.filter((r) => r.isActive === false).length,
        accessPolicy: res.data?.accessPolicy || "RBAC Enabled",
      });
    } catch (error) {
      toast.error("Failed to load roles from backend API");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => {
      fetchRoles();
    }, 300);
    return () => clearTimeout(timer);
  }, [token, cardFilter, search]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete role '${name}'?`)) {
      try {
        await axios.delete(`${server}/roles/deleteById?roleId=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Role deleted successfully");
        fetchRoles();
      } catch (error) {
        toast.error("Error deleting role");
      }
    }
  };

  const openEditModal = (role) => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setSelectedRole(null);
    setIsModalOpen(true);
  };

  const handleCardClick = (filterType) => {
    setCardFilter(filterType);
    setPage(1);
  };

  const totalPages = Math.ceil(roles.length / pageSize) || 1;
  const paginatedRoles = roles.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const getFilterLabel = () => {
    switch (cardFilter) {
      case "active":
        return "Active Roles Only";
      case "inactive":
        return "Inactive Roles Only";
      case "policy":
        return "Access Policy Active Roles";
      default:
        return "All System Roles";
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-700 text-white rounded-xl shadow-xs">
            <FaUserShield size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
                Roles & Permissions Management
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span> Backend API Sync
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Define system access roles, permission levels, and operational privileges
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            onClick={fetchRoles}
            className="p-2 text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition font-bold text-xs"
            title="Refresh Roles API"
          >
            <FaSync className={loading ? "animate-spin text-indigo-500" : ""} size={12} />
          </button>

          <button
            onClick={openCreateModal}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Add New Role
          </button>
        </div>
      </div>

      {/* KPI Stat Cards (Clickable - Triggers Backend API Filtering) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {/* Card 1: Total System Roles */}
        <div
          onClick={() => handleCardClick("all")}
          className={`p-3 rounded-xl border text-center shadow-xs cursor-pointer transition-all duration-200 select-none ${
            cardFilter === "all"
              ? "bg-indigo-50/60 dark:bg-indigo-950/40 border-indigo-500 ring-2 ring-indigo-500/30 scale-[1.02]"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-indigo-300 hover:shadow-xs"
          }`}
          title="Filter All System Roles"
        >
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
            Total System Roles
          </span>
          <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">
            {stats.totalCount}
          </div>
        </div>

        {/* Card 2: Active Roles */}
        <div
          onClick={() => handleCardClick("active")}
          className={`p-3 rounded-xl border text-center shadow-xs cursor-pointer transition-all duration-200 select-none ${
            cardFilter === "active"
              ? "bg-emerald-50/60 dark:bg-emerald-950/40 border-emerald-500 ring-2 ring-emerald-500/30 scale-[1.02]"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-emerald-300 hover:shadow-xs"
          }`}
          title="Filter Active Roles"
        >
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
            Active Roles
          </span>
          <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
            {stats.activeCount}
          </div>
        </div>

        {/* Card 3: Inactive Roles */}
        <div
          onClick={() => handleCardClick("inactive")}
          className={`p-3 rounded-xl border text-center shadow-xs cursor-pointer transition-all duration-200 select-none ${
            cardFilter === "inactive"
              ? "bg-rose-50/60 dark:bg-rose-950/40 border-rose-500 ring-2 ring-rose-500/30 scale-[1.02]"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-rose-300 hover:shadow-xs"
          }`}
          title="Filter Inactive Roles"
        >
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
            Inactive Roles
          </span>
          <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-0.5">
            {stats.inactiveCount}
          </div>
        </div>
      </div>

      {/* Search Toolbar & Filter Indicator */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-80">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition"
            placeholder="Search by Role Name or Description..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {/* Active API Filter Badge */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/50 px-3 py-1 rounded-xl text-xs font-bold text-indigo-700 dark:text-indigo-300">
            <FaFilter className="text-indigo-500 text-[10px]" />
            <span>API Filter: <strong>{getFilterLabel()}</strong></span>
          </div>
          {cardFilter !== "all" && (
            <button
              onClick={() => handleCardClick("all")}
              className="text-[10px] font-extrabold text-gray-500 dark:text-slate-400 hover:text-rose-500 dark:hover:text-rose-400 transition flex items-center gap-1 bg-gray-100 dark:bg-slate-800 px-2.5 py-1 rounded-xl"
              title="Reset Card Filter"
            >
              <FaTimes size={10} /> Reset Filter
            </button>
          )}
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-4">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Role Profile</th>
                <th className="p-2.5">Description & Scope</th>
                <th className="p-2.5">Status</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-12 text-indigo-600 font-bold"
                  >
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Fetching roles from backend API...
                  </td>
                </tr>
              ) : paginatedRoles.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-12 text-gray-400 italic"
                  >
                    No matching roles found.
                  </td>
                </tr>
              ) : (
                paginatedRoles.map((role) => (
                  <tr
                    key={role.id}
                    className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition"
                  >
                    <td className="p-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-950 text-purple-600 dark:text-purple-400 font-black text-xs flex items-center justify-center border border-purple-200 dark:border-purple-800 shrink-0">
                          {role.name?.[0]?.toUpperCase() || "R"}
                        </div>
                        <div>
                          <div className="font-black text-gray-900 dark:text-white text-xs">
                            {role.name}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono font-medium">
                            ID: #{role.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className="p-2.5 text-gray-600 dark:text-slate-300 font-medium max-w-xs truncate"
                      title={role.description}
                    >
                      {role.description || "-"}
                    </td>
                    <td className="p-2.5 font-bold">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-black transition ${
                          role.isActive !== false
                            ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400"
                            : "bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400"
                        }`}
                      >
                        {role.isActive !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => setPermissionsRole(role)}
                          className="px-2 py-1 text-purple-600 bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 transition text-[10px] font-bold flex items-center gap-1"
                          title="Manage Module Access Permissions"
                        >
                          <FaShieldAlt size={10} /> Permissions
                        </button>
                        <button
                          onClick={() => openEditModal(role)}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900 transition"
                          title="Edit Role"
                        >
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(role.id, role.name)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900 transition"
                          title="Delete Role"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Bar */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 text-xs font-bold text-gray-700 dark:text-slate-300">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-slate-800 rounded-lg disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
            >
              <FaChevronLeft size={10} />
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-slate-800 rounded-lg disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
            >
              <FaChevronRight size={10} />
            </button>
          </div>
        </div>
      )}

      {/* Role Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <RoleForm
            role={selectedRole}
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchRoles}
          />
        </div>
      )}
      {/* Permissions Matrix Modal */}
      {permissionsRole && (
        <RolePermissionsModal
          role={permissionsRole}
          onClose={() => setPermissionsRole(null)}
        />
      )}
    </div>
  );
}


